import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loader from 'Components/Forms/Loader';
import React, { FC, Fragment, PropsWithChildren } from 'react';
import { InView } from 'react-intersection-observer';
import ScrollableTableContainer from './ScrollableTableContainer';

interface IInfiniteScrollWrapper extends PropsWithChildren {
  hasMore: boolean;
  scrollThreshold?: number;
  height?: number | string;
  maxHeight?: number | string;
  LoadingIndicatorContainer?: FC<PropsWithChildren>;
  ScrollWrapperComponent?: FC<PropsWithChildren<BoxProps>>;
  LoadingIndicator?: FC;
  next: () => void;
}

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: 60,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const DefaultLoadingIndicator: FC = () => (
  <LoadingContainer>
    <Loader size={24} />
  </LoadingContainer>
);

const InfiniteScrollWrapper: FC<IInfiniteScrollWrapper> = ({
  children,
  hasMore,
  scrollThreshold = 0.8,
  ScrollWrapperComponent = ScrollableTableContainer,
  LoadingIndicatorContainer = Fragment,
  LoadingIndicator = DefaultLoadingIndicator,
  height,
  maxHeight,
  next,
}) => {
  const handleNext = (inView: boolean) => {
    if (inView && hasMore) {
      next();
    }
  };

  return (
    <ScrollWrapperComponent height={height} maxHeight={maxHeight}>
      {children}
      {hasMore && (
        <LoadingIndicatorContainer>
          <InView
            // Fix re-rendering issue by providing a key that changes when children length changes
            // This fixes the issue where the InView component would not re-check visibility when new items are added and inView is still visible
            key={`infinite-scroll-wrapper-inview-${React.Children.count(children)}`}
            trackVisibility
            as="div"
            delay={200}
            // Remove margin from view item to remove 16px gap from parent container
            style={{ margin: '-8px' }}
            threshold={scrollThreshold}
            initialInView
            onChange={handleNext}
          />
          <LoadingIndicator />
        </LoadingIndicatorContainer>
      )}
    </ScrollWrapperComponent>
  );
};

export default InfiniteScrollWrapper;
